import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  TextField, 
  Button,
  FormControlLabel,
  Checkbox,
  Link,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  IconButton
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import { useNavigate } from 'react-router-dom';
import Divider from '@mui/material/Divider';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '964776120413-ei5cuq1l62t8sgid029vbh691kqi3rda.apps.googleusercontent.com';

const Login = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [showPassword, setShowPassword] = useState(false);

  // Check if user is already logged in on page load
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log("User already logged in, redirecting to dashboard...");
        
        // If user is already logged in, redirect to dashboard
        // Use window.location for cross-container navigation
        window.location.href = '/dashboard';
       
      } catch (error) {
        console.error("Error parsing stored user:", error);
        // If stored user data is invalid, clear localStorage
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('companyId');
      }
    }
  }, []); // Removed navigate from dependencies since we're using window.location
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      console.log("API URL:", process.env.REACT_APP_API_URL);
    
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ 
          email, 
          password 
        }),
        mode: 'cors',
        credentials: 'include' // Include cookies if your API uses them
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Store authentication token
        localStorage.setItem('authToken', data.token);

        // Store user data
        localStorage.setItem('user', JSON.stringify(data.user));

        // Store company ID if available
        if (data.user && data.user.companyId) {
          localStorage.setItem('companyId', data.user.companyId.toString());
        }

        // Show success message
        setSnackbarSeverity('success');
        setSnackbarMessage('登入成功！正在跳轉...');
        setOpenSnackbar(true);

        // Dispatch custom event to notify other components about auth change
        window.dispatchEvent(new Event('authChange'));

        // IMPORTANT: Use window.location.href for cross-container navigation
        // This triggers a full page reload and allows NGINX to route to the admin frontend
        // Admin frontend Layout.js 會從 /api/auth/me 取得 planSettings 並處理方案導向
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1000); // Small delay to show success message
        
      } else {
        // Display error message from backend
        setError(data.message || '登入失敗，請檢查您的郵箱和密碼');
        setSnackbarSeverity('error');
        setSnackbarMessage(data.message || '登入失敗，請檢查您的郵箱和密碼');
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('登入時出現錯誤，請稍後再試');
      setSnackbarSeverity('error');
      setSnackbarMessage('登入時出現錯誤，請稍後再試');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    if (!credentialResponse.credential) return;
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: credentialResponse.credential }),
        mode: 'cors',
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        // 未註冊用戶 → 導向註冊頁
        if (response.status === 404) {
          setSnackbarSeverity('info');
          setSnackbarMessage('此帳號尚未註冊，正在跳轉至註冊頁面...');
          setOpenSnackbar(true);
          setTimeout(() => { navigate('/register'); }, 1500);
          return;
        }
        throw new Error(errorData.message || 'Google 登入失敗');
      }

      const data = await response.json();

      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.user?.companyId) {
        localStorage.setItem('companyId', data.user.companyId.toString());
      }

      window.dispatchEvent(new Event('authChange'));

      setSnackbarSeverity('success');
      setSnackbarMessage('Google 登入成功！');
      setOpenSnackbar(true);

      // Admin frontend Layout.js 會從 /api/auth/me 取得 planSettings 並處理方案導向
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (err) {
      setError(err.message || 'Google 登入失敗');
      setSnackbarSeverity('error');
      setSnackbarMessage(err.message || 'Google 登入失敗');
      setOpenSnackbar(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setOpenSnackbar(false);
  };

  const handleRegisterClick = () => {
    // Use React Router for navigation within the same container (frontend-public)
    navigate('/register');
  };

  const handleBackToHome = () => {
    // Navigate back to landing page
    navigate('/');
  };

  return (
    <GoogleOAuthProvider clientId={googleClientId}>
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexGrow: 1,
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}
          >
            {/* Logo/Title */}
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold', mb: 1 }}>
              AI 代理人
            </Typography>
            <Typography variant="h5" component="h2" sx={{ mb: 4 }}>
              歡迎登入
            </Typography>

            {/* Error Alert */}
            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Login Form */}
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="電子郵件"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ mb: 2 }}
                disabled={isLoading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="密碼"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 3 }}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    value="remember"
                    color="primary"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label="記住帳號"
                sx={{ mb: 3 }}
              />
              
              {/* Submit Button */}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={isLoading}
                sx={{ 
                  py: 1.5, 
                  mb: 2, 
                  bgcolor: '#145da0',
                  '&:hover': {
                    bgcolor: '#125390',
                  }
                }}
              >
                {isLoading ? <CircularProgress size={24} color="inherit" /> : '登入'}
              </Button>
              
              <Divider sx={{ my: 2 }}>
                <Typography variant="body2" color="text.secondary">或</Typography>
              </Divider>

              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                {isLoading ? (
                  <CircularProgress size={24} sx={{ color: '#4285F4' }} />
                ) : (
                  <GoogleLogin
                    onSuccess={handleGoogleLoginSuccess}
                    onError={() => { setError('Google 登入失敗'); }}
                    useOneTap={false}
                    shape="rectangular"
                    text="signin_with"
                    locale="zh_TW"
                    width="400"
                    theme="outline"
                    size="large"
                  />
                )}
              </Box>

              {/* Links */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Link
                  variant="body2"
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                  onClick={handleBackToHome}
                >
                  返回首頁
                </Link>
                <Link
                  variant="body2"
                  underline="hover"
                  sx={{ cursor: 'pointer' }}
                  onClick={handleRegisterClick}
                >
                  註冊帳號
                </Link>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
    </GoogleOAuthProvider>
  );
};

export default Login;